/**
 * Injects pre-order tags on Advanced Commerce / GrapheneHC PLP cards.
 * Relies on fetchProductMetafieldData + isPreOrderLeadTime from lead-time.js.
 */

const preOrderMetafieldCache = new Map();
let plpPreOrderObserverInitialized = false;

function getPreOrderPlpLabel() {
  return window.theme?.preOrder?.plpLabel || 'Pre-order – Reserve now';
}

function getProductHandleFromPlpCard(cardRoot) {
  const link =
    cardRoot.querySelector('.card__heading a') ||
    cardRoot.querySelector('.prd-Card_TitleLink') ||
    cardRoot.querySelector('a[href*="/products/"]');

  const href = link?.getAttribute('href');
  if (!href) return null;

  const match = href.match(/\/products\/([^/?#]+)/);
  return match?.[1] || null;
}

function isPlpPromotionCard(gridItem) {
  return (
    gridItem.classList.contains('promotion__item') ||
    gridItem.querySelector('.promotion-combined_collection') != null
  );
}

function getPlpCardInformation(gridItem) {
  return gridItem.querySelector('.card__information');
}

function getPlpColoursAnchor(information) {
  return (
    information.querySelector('card-colours.prd-Card_Colours') ||
    information.querySelector('.prd-Card_Colours') ||
    information.querySelector('card-colours')
  );
}

function createPlpPreOrderTag() {
  const tag = document.createElement('span');
  tag.className = 'prd-PreOrder_Tag prd-PreOrder_Tag--plp';
  tag.setAttribute('data-ab-pre-order-tag', '');
  tag.textContent = getPreOrderPlpLabel();
  return tag;
}

function insertPlpPreOrderTag(information, tag) {
  const colours = getPlpColoursAnchor(information);
  if (colours) {
    colours.insertAdjacentElement('afterend', tag);
    return;
  }

  const reviews = information.querySelector('.prd-Card_Review');
  if (reviews) {
    reviews.insertAdjacentElement('beforebegin', tag);
    return;
  }

  information.appendChild(tag);
}

async function getCachedProductMetafields(handle) {
  if (preOrderMetafieldCache.has(handle)) {
    return preOrderMetafieldCache.get(handle);
  }

  if (typeof fetchProductMetafieldData !== 'function') {
    return null;
  }

  const promise = fetchProductMetafieldData(handle).catch(() => null);
  preOrderMetafieldCache.set(handle, promise);
  return promise;
}

async function enrichPlpCardPreOrderTag(gridItem) {
  if (isPlpPromotionCard(gridItem)) return;

  const information = getPlpCardInformation(gridItem);
  if (!information) return;

  if (information.dataset.preOrderEnriched === 'true') return;
  if (information.dataset.preOrderPending === 'true') return;

  if (information.querySelector('[data-ab-pre-order-tag]')) {
    information.dataset.preOrderEnriched = 'true';
    return;
  }

  const handle = getProductHandleFromPlpCard(gridItem);
  if (!handle) return;

  information.dataset.preOrderPending = 'true';

  try {
    const metafields = await getCachedProductMetafields(handle);
    if (!metafields || typeof window.isPreOrderLeadTime !== 'function') return;
    if (!window.isPreOrderLeadTime(metafields, 1)) return;

    insertPlpPreOrderTag(information, createPlpPreOrderTag());
    information.dataset.preOrderEnriched = 'true';
  } catch (error) {
    console.error('Pre-order PLP tag failed:', handle, error);
  } finally {
    delete information.dataset.preOrderPending;
  }
}

async function enrichAcPlpPreOrderTags(root = document) {
  const scopes = [];

  if (root instanceof Element && root.matches('.acs_collection_results, .acs_search_results, .acs_results')) {
    scopes.push(root);
  } else if (root instanceof Element) {
    root.querySelectorAll('.acs_collection_results, .acs_search_results, .acs_results').forEach((el) => scopes.push(el));
  } else {
    document.querySelectorAll('.acs_collection_results, .acs_search_results, .acs_results').forEach((el) => scopes.push(el));
  }

  if (!scopes.length && root instanceof Element) {
    scopes.push(root);
  }

  const gridItems = new Set();

  for (const scope of scopes) {
    for (const item of scope.querySelectorAll('.grid__item')) {
      gridItems.add(item);
    }
  }

  if (!gridItems.size && root instanceof Element) {
    for (const item of root.querySelectorAll('.grid__item')) {
      gridItems.add(item);
    }
  }

  await Promise.all([...gridItems].map((gridItem) => enrichPlpCardPreOrderTag(gridItem)));
}

function debouncePlpPreOrderEnrich(fn, wait) {
  let timeoutId;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => fn(...args), wait);
  };
}

const debouncedEnrichAcPlpPreOrderTags = debouncePlpPreOrderEnrich(() => {
  enrichAcPlpPreOrderTags();
}, 200);

function initAcPlpPreOrderObserver() {
  if (plpPreOrderObserverInitialized) return;

  const targets = document.querySelectorAll('.acs_collection_results, .acs_search_results, .acs_results');
  if (!targets.length) return;

  plpPreOrderObserverInitialized = true;

  for (const target of targets) {
    const observer = new MutationObserver(() => {
      debouncedEnrichAcPlpPreOrderTags();
    });
    observer.observe(target, { childList: true, subtree: true });
  }
}

document.addEventListener('swym:collections-loaded', () => {
  enrichAcPlpPreOrderTags();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAcPlpPreOrderObserver, { once: true });
} else {
  initAcPlpPreOrderObserver();
}

window.enrichAcPlpPreOrderTags = enrichAcPlpPreOrderTags;
